import React, { useState } from 'react';
import { SlSidebar, SlSidebarSectionContainer, SlSidebarTitle } from 'src/components/BeaconV2Components/SlSidebar';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { Box } from '@mui/system';
import { hexToRgba, useStacklineTheme } from '@stackline/ui';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import {
  FILTER_OPERATION,
  useSavedSearchBuilder
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/hooks/useSavedSearchBuilder';
import { BeaconProSidebarFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Filters/constants';
import LegacySidebarFilter from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentsV2/LegacySidebarFilter';
import LegacyBrandSidebarFilter from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentsV2/LegacyBrandSidebarFilter';
import LegacyCategorySidebarFilter from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentsV2/LegacyCategorySidebarFilter';
import LegacySubcategorySidebarFilter from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentsV2/LegacySubcategorySidebarFilter';
import { ENTITIES } from 'src/utils/entityDefinitions';
import { AppName } from 'sl-api-connector';
import { useAllSearchTermLists } from 'src/utils/Hooks/reduxSelectors';
import { updateSavedSearch as updateTopics } from 'src/store/modules/segments/operations';
import { useSnackbar } from 'src/utils/Hooks';
import { useDispatch } from 'react-redux';
import { AxiosResponse } from 'axios';
import { SegmentCreationSnackbar } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentCreationSnackbar';
import { SegmentBuilderBulkUploadModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentsV2/SegmentBuilderBulkUploadModal';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import SidebarInput from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/SidebarInput';
import { GenericErrorMessage } from 'src/components/BeaconV2Components/common/CommonSnackbarMessages/GenericErrorMessage';
import { shouldShowNewAtlas } from 'src/utils/app';
import AtlasV2BrandSidebarFilter from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentsV2/AtlasV2BrandSidebarFilter';

/**
 * Filter group for the Segment Builder sidebar.
 */
const TopicBuilderSidebarFilters = () => {
  return (
    <>
      {/* Include - Phrase */}
      <Box sx={{ marginTop: '32px' }}>
        <LegacySidebarFilter
          filterParameter={BeaconProSidebarFilters.SearchTermFuzzy.filterKey}
          transferableParameter={BeaconProSidebarFilters.KeywordPhrase.filterKey}
          title="Include - Phrase"
          sidebarInputProps={{ placeholder: 'Keyword phrase to include' }}
          includeClear
        />
      </Box>

      {/* Include - Exact */}
      <LegacySidebarFilter
        filterParameter={BeaconProSidebarFilters.SearchTerm.filterKey}
        title="Include - Exact"
        sidebarInputProps={{ placeholder: 'Exact keyword to include' }}
        includeClear
      />

      {/* Exclude - Phrase */}
      <LegacySidebarFilter
        filterParameter={BeaconProSidebarFilters.ExcludedSearchTermFuzzy.filterKey}
        title="Exclude - Phrase"
        sidebarInputProps={{ placeholder: 'Keyword phrase to exclude' }}
        includeClear
      />

      {/* Exclude - Exact */}
      <LegacySidebarFilter
        filterParameter={BeaconProSidebarFilters.ExcludedSearchTerm.filterKey}
        title="Exclude - Exact"
        sidebarInputProps={{ placeholder: 'Exact keyword to exclude' }}
        includeClear
      />

      {/* Brands */}
      {shouldShowNewAtlas() ? (
        <AtlasV2BrandSidebarFilter
          title="Brands"
          sidebarInputProps={{ placeholder: 'Refine by brands' }}
          includeClear
        />
      ) : (
        <LegacyBrandSidebarFilter title="Brands" sidebarInputProps={{ placeholder: 'Refine by brands' }} includeClear />
      )}

      {/* Categories */}
      <Box sx={{ marginTop: '32px' }}>
        <LegacyCategorySidebarFilter
          title="Categories"
          sidebarInputProps={{ placeholder: 'Refine by categories' }}
          includeClear
          includeToggles
        />
      </Box>

      {/* Subcategories */}
      <Box sx={{ marginTop: '32px' }}>
        <LegacySubcategorySidebarFilter
          title="Subcategories"
          sidebarInputProps={{ placeholder: 'Refine by subcategories' }}
          includeClear
          includeToggles
        />
      </Box>
    </>
  );
};

/**
 * Memoized version of the TopicBuilderSidebarFilters component to avoid unnecessary rerenders
 */
const MemoizedTopicFilters = React.memo(TopicBuilderSidebarFilters);

export const LegacyTopicBuilderSidebar = () => {
  const { updateSavedSearch, formData } = useSavedSearchBuilder();
  const [name, setName] = useState(formData?.dn || '');

  const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const theme = useStacklineTheme();
  const allSearchTermLists = useAllSearchTermLists();

  const { showSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const { beaconNavigator } = useBeaconNavigator();

  const handleSaveTopic = async () => {
    try {
      const segmentName = name.trim();
      if (segmentName) {
        setIsSaving(true);
        const savedSearchPayload = { ...formData, dn: segmentName };
        const entityDefinition = ENTITIES[AppName.Atlas]?.searchtermlist;
        const lowerCaseDisplayName = (name || '').toLowerCase();

        const hasNameCollision = !!Array.from(allSearchTermLists.values()).find((savedSearch) => {
          return savedSearch.id !== formData.id && savedSearch.displayName.toLowerCase() === lowerCaseDisplayName;
        });

        if (hasNameCollision) {
          showSnackbar({
            message: 'You must pick a unique name for this topic; a topic with the same name already exists.',
            type: 'error'
          });
          setIsSaving(false);
          return false;
        }

        const response = (await dispatch(
          updateTopics(entityDefinition.type, savedSearchPayload, formData.id, null, true, {})
        )) as unknown as AxiosResponse;
        setIsSaving(false);
        // If the topic was saved successfully, navigate to the topic
        beaconNavigator.setPathname(`searchtermlist/${response?.data}`);
        beaconNavigator.addSearchParams({ tab: ['traffic'], subtab: ['totalClicks'] });
        beaconNavigator.go();

        showSnackbar({
          type: 'success',
          message: <SegmentCreationSnackbar isTopicBuilder />
        });
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error saving segment', error);
      setIsSaving(false);
      showSnackbar({
        message: <GenericErrorMessage />,
        type: 'error'
      });
    }

    return null;
  };

  return (
    <SlSidebar gap={0} sx={{ width: 'auto' }}>
      <SlSidebarSectionContainer gap={0}>
        <Text sx={{ marginBottom: '32px', marginTop: '54px' }} variant="h4">
          Filter
        </Text>

        <SlSidebarTitle>Name</SlSidebarTitle>
        <SidebarInput
          style={{ marginTop: '8px' }}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name your topic"
        />
      </SlSidebarSectionContainer>

      <MemoizedTopicFilters />
      <SegmentBuilderBulkUploadModal
        isTopic
        isLegacyTopic
        open={bulkUploadModalOpen}
        handleClose={() => setBulkUploadModalOpen(false)}
      />
      <Flex flexDirection="column" marginTop="64px" gap={16}>
        <SlButton onClick={handleSaveTopic} disabled={!name.trim() || isSaving} variant="contained">
          {isSaving ? 'Saving...' : 'Save'}
        </SlButton>
        <SlButton
          onClick={() => setBulkUploadModalOpen(true)}
          variant="tertiary"
        >
          Bulk Upload
        </SlButton>
        <SlButton
          onClick={() => {
            updateSavedSearch({ key: null, newValue: null, operation: FILTER_OPERATION.Clear_All });
          }}
        >
          Clear Filters
        </SlButton>
      </Flex>
    </SlSidebar>
  );
};
